import { useSuspenseFragment } from 'react-relay-offline'
import { graphql } from 'babel-plugin-relay/macro'
import React, { Suspense, useMemo } from 'react'
import { csx } from '@vtex/admin-ui'
import { FormattedMessage } from 'react-intl'

import { useProductPageUrlParams } from '../../hooks'
import { AddToCartSection } from '../AddToCartSection'
import type { ProductSkuSelection_Product$key } from './__generated__/ProductSkuSelection_Product.graphql'
import {
  ProductSpecificationsSkuSelector,
  useVariations,
} from '../../../../core/components'
import { ProductSkuSelectionLoading } from './ProductSkuSelectionLoading'
import { ProductSkuOtherOffers } from '../ProductSkuOtherOffers'
import { ProductSkuAlert } from '../ProductSkuAlert'

const productFragment = graphql`
  fragment ProductSkuSelection_Product on Product {
    name
    productSkuSpecifications {
      edges {
        node {
          name
          values
        }
      }
    }
    skus {
      edges {
        node {
          _id
          id
          name
          variations {
            edges {
              node {
                name
                values
              }
            }
          }
          offers(addresses: $addresses) {
            edges {
              __typename
            }
          }
        }
      }
    }
    ...ProductSkuOtherOffers_Product
  }
`

type ProductSkuSelectionContentProps = {
  productRef: ProductSkuSelection_Product$key
}

const ProductSkuSelectionContent = ({
  productRef,
}: ProductSkuSelectionContentProps) => {
  const { selectedSkuId, setSelectedSkuId } = useProductPageUrlParams()
  const product = useSuspenseFragment(productFragment, productRef)

  const productSkuSpecifications = useMemo(() => {
    return product.productSkuSpecifications.edges.map(({ node }) => ({
      name: node.name,
      values: node.values,
    }))
  }, [product.productSkuSpecifications.edges])

  const productSkus = useMemo(() => {
    return product.skus.edges.map(({ node }) => ({
      _id: node._id,
      id: node.id,
      name: node.name,
      variations: node.variations.edges.map((variationEdge) => ({
        name: variationEdge.node.name,
        values: variationEdge.node.values,
      })),
      hasOffer: !!node.offers?.edges.length,
    }))
  }, [product.skus.edges])

  const { variations, selectedSku, handleVariationChange } = useVariations({
    productSkuSpecifications,
    productSkus,
    initialSelectedSkuId: selectedSkuId,
    onVariationChange: (sku) => {
      setSelectedSkuId(sku.id)
    },
  })

  const hasOffer = productSkus.find((sku) => sku.id === selectedSku?.id)
    ?.hasOffer

  return (
    <>
      <div
        className={csx({ marginBottom: '$space-8' })}
        data-testid="product-sku-specifications"
      >
        <ProductSpecificationsSkuSelector
          variations={variations}
          onVariationChange={handleVariationChange}
        />
      </div>
      <ProductSkuOtherOffers productRef={product} />
      {!hasOffer && (
        <ProductSkuAlert type="warning">
          <FormattedMessage id="checkout.unavailable-product.alert" />
        </ProductSkuAlert>
      )}
      <AddToCartSection selectedSku={selectedSku} hasOffer={hasOffer} />
    </>
  )
}

export type ProductSkuSelectionProps = {
  productRef: ProductSkuSelection_Product$key
}

export const ProductSkuSelection = ({
  productRef,
}: ProductSkuSelectionProps) => (
  <Suspense fallback={<ProductSkuSelectionLoading />}>
    <ProductSkuSelectionContent productRef={productRef} />
  </Suspense>
)
